// contactInfoService.ts
import { REACT_APP_WUCA_DXP_ADMIN_URL, REACT_APP_WUCA_DXP_API_BEARER_TOKEN } from "./config";
import { logger } from "./logger";

export interface ContactInfo {
  name: string;
  title: string;
  email: string;
  phoneNumber: string;
}

export async function fetchContactInfo({
  authToken,
  silentTokenAcquireHook,
}: {
  authToken: string;
  silentTokenAcquireHook?: () => Promise<boolean>;
}): Promise<ContactInfo[]> {
  const apiUrl = `${REACT_APP_WUCA_DXP_ADMIN_URL}api/v1/contactinfo`;
  const MAX_RELOGIN_ATTEMPTS = 2;
  let reloginAttempts = 0;

  if (!authToken) {
    const errorMessage = "No authentication token provided";
    logger.logToApi({
      type: "Error",
      source: "contactInfoService",
      eventCode: "MISSING_AUTH_TOKEN",
      error: errorMessage,
    }, silentTokenAcquireHook);
    throw new Error(errorMessage);
  }

  const makeRequest = async (token: string): Promise<ContactInfo[]> => {
    const response = await fetch(apiUrl, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        "X-API-KEY": REACT_APP_WUCA_DXP_API_BEARER_TOKEN,
        accept: "text/plain",
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      const errorMessage = `API error: ${response.status}`;
      const error = new Error(errorMessage) as any;
      error.response = { status: response.status };
      logger.logToApi({
        type: "Error",
        source: "contactInfoService",
        eventCode: "FETCH_CONTACTINFO_API_ERROR",
        error: errorMessage,
      }, silentTokenAcquireHook);
      throw error;
    }

    return await response.json();
  };

  try {
    const data = await makeRequest(authToken);
    reloginAttempts = 0;
    return data;
  } catch (error: any) {
    console.error("Error fetching contact info:", error);
    if (
      (error?.response?.status === 401 || error?.response?.status === 403) &&
      reloginAttempts < MAX_RELOGIN_ATTEMPTS &&
      silentTokenAcquireHook
    ) {
      reloginAttempts++;
      const isReLogined = await silentTokenAcquireHook();
      
      if (isReLogined) {
        try {
          const newToken = sessionStorage.getItem("authToken") || "";
          const data = await makeRequest(newToken);
          reloginAttempts = 0;
          return data;
        } catch (retryError: any) {
          reloginAttempts = 0;
          logger.logToApi({
            type: "Error",
            source: "contactInfoService",
            eventCode: "FETCH_CONTACTINFO_RETRY_FAILED",
            error: `Retry failed: ${retryError.message}\n${retryError.stack || ""}`,
          }, silentTokenAcquireHook);
          throw retryError;
        }
      }
    } else {
      reloginAttempts = 0;
      if (!error.message?.includes("API error:")) {
        logger.logToApi({
          type: "Error",
          source: "contactInfoService",
          eventCode: "FETCH_CONTACTINFO_UNEXPECTED_ERROR",
          error: `${error.message}\n${error.stack || ""}`,
        }, silentTokenAcquireHook);
      }
      throw error;
    }
  }
  throw new Error("fetchContactInfo: Unexpected code path reached without returning ContactInfo[].");
}